import React, { useState, useEffect, useRef, useCallback } from "react";
import { Settings as SettingsIcon, Save, Upload, Trash2, Palette, CreditCard, Eye, EyeOff, Receipt, ToggleLeft, Percent, Building2, Mail, Send, FileText } from "lucide-react";
import { Form, Row, Col, Modal } from "react-bootstrap";
import { PageHeader, AppButton } from "../../ui";
import { DetailRow } from "../../ui/DetailRow";
import apiClient from "../../../utils/apiClient";
import "./settings.scss";

const TABS = [
  { key: "general", label: "General", icon: SettingsIcon },
  { key: "branding", label: "Branding", icon: Palette },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "billing", label: "Billing", icon: Building2 },
  { key: "email", label: "Email", icon: Mail },
  { key: "info", label: "Info", icon: FileText },
];

/**
 * SettingsPage — Platform-level settings for the SuperAdmin.
 */
const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState("general");
  const [settings, setSettings] = useState({
    platformName: "VirtualClassroom",
    supportEmail: "",
    defaultTrialDays: 14,
    maxStudentsPerInstitute: 500,
    maintenanceMode: false,
    signupEnabled: true,
    autoApproveInstitutes: false,
    brandName: "LMS Campus",
    brandSubtitle: "Learning Management",
    primaryColor: "#2563eb",
    logoBgColor: "#3b82f6",
    disabledFeatures: [],
  });
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [settingsRes, logoRes, faviconRes] = await Promise.all([
          apiClient.get("/sa/settings"),
          apiClient.get("/sa/settings/logo-url"),
          apiClient.get("/sa/settings/favicon-url"),
        ]);
        if (settingsRes.data.settings) {
          setSettings(settingsRes.data.settings);
          if (settingsRes.data.settings.billing) setBilling(prev => ({ ...prev, ...settingsRes.data.settings.billing }));
        }
        setLogoUrl(logoRes.data.url || null);
        setFaviconUrl(faviconRes.data.url || null);
      } catch { /* use defaults */ }
    })();
  }, []);

  const update = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setSettings((prev) => ({ ...prev, [key]: val }));
  };

  const toggleDisabledFeature = (key) => {
    setSettings(prev => {
      const cur = prev.disabledFeatures || [];
      const has = cur.includes(key);
      return { ...prev, disabledFeatures: has ? cur.filter(f => f !== key) : [...cur, key] };
    });
  };

  const fetchRzSettings = async () => {
    try {
      const { data } = await apiClient.get("/sa/razorpay");
      setRz(data);
    } catch { /* defaults */ }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Save general settings (including commission which is on the Payments tab — saved together with general)
      await apiClient.put("/sa/settings", { ...settings, commission, billing });
      setRzMsg({ type: "success", text: "Settings saved!" });
    } catch (err) {
      setRzMsg({ type: "error", text: err?.response?.data?.message || "Failed to save." });
    }
    finally { setSaving(false); }
  };

  const handlePaymentsSave = async () => {
    try {
      setSaving(true);
      await Promise.all([
        apiClient.put("/sa/razorpay", rz),
        apiClient.put("/sa/tax", tax),
        apiClient.put("/sa/settings", { ...settings, commission }),
      ]);
      setRzMsg({ type: "success", text: "Payment settings saved!" });
      await Promise.all([fetchRzSettings(), fetchTaxSettings()]);
    } catch (err) {
      setRzMsg({ type: "error", text: err?.response?.data?.message || "Failed to save." });
    }
    finally { setSaving(false); }
  };

  const handleBillingSave = async () => {
    try {
      setSaving(true);
      await apiClient.put("/sa/settings", { ...settings, billing });
      setRzMsg({ type: "success", text: "Billing details saved!" });
    } catch (err) {
      setRzMsg({ type: "error", text: err?.response?.data?.message || "Failed to save." });
    }
    finally { setSaving(false); }
  };

  // ── Plan Invoice Preview (dummy data) ──
  const [invoicePreviewUrl, setInvoicePreviewUrl] = useState(null);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);

  const handleInvoicePreview = async () => {
    try {
      setInvoicePreviewLoading(true);
      const res = await apiClient.get("/sa/billing/plan-invoice-preview", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setInvoicePreviewUrl(url);
      setShowInvoicePreview(true);
    } catch (err) {
      setRzMsg({ type: "error", text: err?.response?.data?.message || "Failed to generate preview." });
    }
    setInvoicePreviewLoading(false);
  };

  const closeInvoicePreview = useCallback(() => {
    setShowInvoicePreview(false);
    if (invoicePreviewUrl) { URL.revokeObjectURL(invoicePreviewUrl); setInvoicePreviewUrl(null); }
  }, [invoicePreviewUrl]);

  /* ── Logo upload via server-side upload ── */
  const handleLogoUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      await apiClient.post("/sa/settings/logo-upload-url", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const logoRes = await apiClient.get("/sa/settings/logo-url");
      setLogoUrl(logoRes.data.url || null);
    } catch { /* handle error */ }
  };

  const handleLogoDelete = async () => {
    try {
      await apiClient.delete("/sa/settings/logo");
      setLogoUrl(null);
    } catch { /* handle error */ }
  };

  /* ── Favicon upload via server-side upload ── */
  const handleFaviconUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      await apiClient.post("/sa/settings/favicon-upload-url", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const favRes = await apiClient.get("/sa/settings/favicon-url");
      setFaviconUrl(favRes.data.url || null);
    } catch { /* handle error */ }
  };

  const handleFaviconDelete = async () => {
    try {
      await apiClient.delete("/sa/settings/favicon");
      setFaviconUrl(null);
    } catch { /* handle error */ }
  };

  // ── Razorpay Platform Config ──
  const [rz, setRz] = useState({
    enabled: false,
    mode: "test",
    testKeyId: "",
    testKeySecret: "",
    liveKeyId: "",
    liveKeySecret: "",
    webhookSecret: "",
  });
  const [rzLoading, setRzLoading] = useState(true);
  const [rzSaving, setRzSaving] = useState(false);
  const [rzMsg, setRzMsg] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});

  // ── Commission Config ──
  const [commission, setCommission] = useState({
    enabled: false,
    type: "percentage",
    percentage: 10,
    flatFee: 0,
    minimumCommission: 0,
    maximumCommission: 0,
  });

  // ── Tax / GST Config ──
  const [tax, setTax] = useState({
    enabled: false,
    label: "GST",
    percentage: 18,
    gstNumber: "",
    businessName: "",
    includeInPrice: false,
  });

  // ── Billing / Company Details (for service invoices) ──
  const [billing, setBilling] = useState({
    companyName: "",
    companyAddress: "",
    companyCity: "",
    companyState: "",
    companyStateCode: "",
    companyPincode: "",
    companyPan: "",
    companyGstin: "",
    sacCode: "998314",
    enableGst: true,
    gstRate: 18,
    companyPhone: "",
    companyEmail: "",
    companyWebsite: "",
    invoicePrefix: "LMS",
    reverseCharge: false,
    bankName: "",
    bankAccountName: "",
    bankAccountNumber: "",
    bankIfscCode: "",
    bankBranch: "",
    invoiceTerms: "Payment due within 7 days from invoice date.\nNo refunds once service is activated.\nThis is a system-generated invoice and does not require a signature.",
  });

  const billingUpdate = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setBilling((prev) => ({ ...prev, [key]: val }));
  };

  // ── Email Settings ──
  const [emailCfg, setEmailCfg] = useState({
    provider: "ses",
    ses: { host: "", port: 587, user: "", hasPassword: false },
    gmail: { user: "", hasPassword: false },
    senderEmail: "noreply@lmscampus.com",
    senderName: "LMS Campus",
    replyToEmail: "",
    maxConnectionPool: 5,
    rateLimit: 14,
    queueDelay: 200,
  });
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [testingSend, setTestingSend] = useState(false);
  const [sesPass, setSesPass] = useState("");
  const [gmailPass, setGmailPass] = useState("");
  const [sesUser, setSesUser] = useState("");

  const fetchEmailSettings = async () => {
    try {
      const { data } = await apiClient.get("/sa/email-settings");
      setEmailCfg(data);
      setSesUser("");
      setSesPass("");
      setGmailPass("");
    } catch { /* defaults */ }
  };

  const emailUpdate = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setEmailCfg((prev) => ({ ...prev, [key]: val }));
  };

  const handleEmailSave = async () => {
    setEmailSaving(true);
    setEmailMsg(null);
    try {
      const payload = {
        provider: emailCfg.provider,
        senderEmail: emailCfg.senderEmail,
        senderName: emailCfg.senderName,
        replyToEmail: emailCfg.replyToEmail,
        maxConnectionPool: emailCfg.maxConnectionPool,
        rateLimit: emailCfg.rateLimit,
        queueDelay: emailCfg.queueDelay,
      };
      if (emailCfg.provider === "ses") {
        payload.ses = {
          host: emailCfg.ses?.host,
          port: emailCfg.ses?.port,
        };
        if (sesUser) payload.ses.user = sesUser;
        if (sesPass) payload.ses.pass = sesPass;
      } else {
        payload.gmail = { user: emailCfg.gmail?.user };
        if (gmailPass) payload.gmail.pass = gmailPass;
      }
      const { data } = await apiClient.put("/sa/email-settings", payload);
      setEmailMsg({ type: "success", text: data.message || "Saved!" });
      await fetchEmailSettings();
    } catch (err) {
      setEmailMsg({ type: "error", text: err?.response?.data?.message || "Failed to save." });
    }
    setEmailSaving(false);
  };

  const handleTestEmail = async () => {
    if (!testEmail) return;
    setTestingSend(true);
    setEmailMsg(null);
    try {
      const { data } = await apiClient.post("/sa/email-settings/test", { testEmail });
      setEmailMsg({ type: "success", text: data.message || "Test sent!" });
    } catch (err) {
      setEmailMsg({ type: "error", text: err?.response?.data?.message || "Test failed." });
    }
    setTestingSend(false);
  };

  const fetchCommissionSettings = async () => {
    try {
      const { data } = await apiClient.get("/sa/settings");
      if (data.settings?.commission) setCommission(data.settings.commission);
    } catch { /* defaults */ }
  };

  const commissionUpdate = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setCommission((prev) => ({ ...prev, [key]: val }));
  };

  const fetchTaxSettings = async () => {
    try {
      const { data } = await apiClient.get("/sa/tax");
      setTax(data);
    } catch { /* defaults */ }
  };

  const taxUpdate = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setTax((prev) => ({ ...prev, [key]: val }));
  };

  useEffect(() => {
    (async () => {
      await Promise.all([fetchRzSettings(), fetchTaxSettings(), fetchCommissionSettings(), fetchEmailSettings()]);
      setRzLoading(false);
    })();
  }, []);

  const rzUpdate = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setRz((prev) => ({ ...prev, [key]: val }));
  };

  const handleRzSave = async () => {
    setRzSaving(true);
    setRzMsg(null);
    try {
      const { data } = await apiClient.put("/sa/razorpay", rz);
      setRzMsg({ type: "success", text: data.message || "Saved!" });
      // Re-fetch to show masked secrets
      await fetchRzSettings();
    } catch (err) {
      setRzMsg({ type: "error", text: err?.response?.data?.message || "Failed to save." });
    }
    setRzSaving(false);
  };

  const toggleSecret = (field) => setShowSecrets((p) => ({ ...p, [field]: !p[field] }));

  return (
    <div className="sa-settings">
      <PageHeader
        title="Platform Settings"
        description="Configure global platform parameters"
        icon={SettingsIcon}
      />

      {/* ── Tab Bar ── */}
      <div className="sa-settings__tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            className={`sa-settings__tab ${activeTab === key ? "sa-settings__tab--active" : ""}`}
            onClick={() => setActiveTab(key)}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* ── Status message ── */}
      {rzMsg && (
        <div className={`alert alert-${rzMsg.type === "success" ? "success" : "danger"} py-1 px-3 mb-0`} style={{ fontSize: 13 }}>
          {rzMsg.text}
        </div>
      )}

      {/* ═══ GENERAL TAB ═══ */}
      {activeTab === "general" && (
        <>
          <div className="sa-card">
            <div className="sa-card-head">
              <ToggleLeft size={16} className="me-2" />Feature Availability
            </div>
            <div className="sa-card-body">
              <p className="sa-settings__hint">
                Turn features on/off globally. Disabled features are completely hidden from all institutes.
                You can re-enable per institute for testing from the Institute Detail page.
              </p>
              <div className="sa-settings__feature-grid">
                {[
                  { key: 'quizAssessment', label: 'Quiz & Assessments' },
                  { key: 'aiMock', label: 'AI Mock Interview' },
                  { key: 'aiLearning', label: 'AI Learning' },
                  { key: 'aiQuestionBank', label: 'AI Question Bank' },
                  { key: 'feeManagement', label: 'Fee Management' },
                  { key: 'enquiryManagement', label: 'Enquiry Management' },
                  { key: 'interviews', label: 'Interviews' },
                  { key: 'codingPractice', label: 'Coding Practice' },
                  { key: 'selfPacedCourses', label: 'Self-Paced Learning' },
                  { key: 'assignments', label: 'Assignments' },
                  { key: 'customBranding', label: 'Custom Branding' },
                  { key: 'customDomain', label: 'Custom Domain' },
                  { key: 'platformHostedZoom', label: 'Platform Hosted Zoom' },
                  { key: 'recordings', label: 'Class Recordings' },
                  { key: 'whatsapp', label: 'WhatsApp Notifications' },
                ].map(({ key, label }) => (
                  <Form.Check
                    key={key}
                    type="switch"
                    id={`feat-${key}`}
                    label={label}
                    checked={!(settings.disabledFeatures || []).includes(key)}
                    onChange={() => toggleDisabledFeature(key)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="sa-card">
            <div className="sa-card-head">General</div>
            <div className="sa-card-body">
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="sa-settings__label">Platform Name</Form.Label>
                    <Form.Control size="sm" value={settings.platformName} onChange={update("platformName")} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="sa-settings__label">Support Email</Form.Label>
                    <Form.Control size="sm" type="email" value={settings.supportEmail} onChange={update("supportEmail")} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="sa-settings__label">Default Trial Days</Form.Label>
                    <Form.Control size="sm" type="number" value={settings.defaultTrialDays} onChange={update("defaultTrialDays")} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="sa-settings__label">Max Students per Institute</Form.Label>
                    <Form.Control size="sm" type="number" value={settings.maxStudentsPerInstitute} onChange={update("maxStudentsPerInstitute")} />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </div>

          <div className="sa-card">
            <div className="sa-card-head">Access & Registration</div>
            <div className="sa-card-body">
              <div className="sa-settings__toggles">
                <Form.Check
                  type="switch"
                  id="signup-enabled"
                  label="Allow new institute sign-ups"
                  checked={settings.signupEnabled}
                  onChange={update("signupEnabled")}
                />
                <Form.Check
                  type="switch"
                  id="auto-approve"
                  label="Auto-approve new institutes (skip manual review)"
                  checked={settings.autoApproveInstitutes}
                  onChange={update("autoApproveInstitutes")}
                />
                <Form.Check
                  type="switch"
                  id="maintenance"
                  label="Maintenance mode (platform offline for non-admin users)"
                  checked={settings.maintenanceMode}
                  onChange={update("maintenanceMode")}
                  className={settings.maintenanceMode ? "text-danger" : ""}
                />
              </div>
            </div>
          </div>

          <div className="sa-settings__tab-save">
            <AppButton variant="primary" size="sm" onClick={handleSave} disabled={saving}>
              <Save size={14} className="me-1" /> {saving ? "Saving…" : "Save General Settings"}
            </AppButton>
          </div>
        </>
      )}

      {/* ═══ BRANDING TAB ═══ */}
      {activeTab === "branding" && (
        <>
          <div className="sa-card">
            <div className="sa-card-head">
              <Palette size={16} className="me-2" />LMS Branding
            </div>
        <div className="sa-card-body">
          <p style={{ fontSize: "0.8rem", color: "var(--text-tertiary, #9ca3af)", marginBottom: "1rem" }}>
            This branding is shown on login, signup, and registration pages for institutes that don't use custom branding.
          </p>

          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">LMS Name</Form.Label>
                <Form.Control size="sm" value={settings.brandName || ""} onChange={update("brandName")} placeholder="e.g. LMS Campus" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Subtitle</Form.Label>
                <Form.Control size="sm" value={settings.brandSubtitle || ""} onChange={update("brandSubtitle")} placeholder="e.g. Learning Management" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Primary Color</Form.Label>
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="color"
                    value={settings.primaryColor || "#2563eb"}
                    onChange={(e) => setSettings((p) => ({ ...p, primaryColor: e.target.value }))}
                    style={{ width: 36, height: 36, padding: 2, border: "1px solid #e5e7eb", borderRadius: 6, cursor: "pointer" }}
                  />
                  <Form.Control size="sm" value={settings.primaryColor || "#2563eb"} onChange={update("primaryColor")} maxLength={7} style={{ maxWidth: 120 }} />
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Logo Background Color</Form.Label>
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="color"
                    value={settings.logoBgColor || "#3b82f6"}
                    onChange={(e) => setSettings((p) => ({ ...p, logoBgColor: e.target.value }))}
                    style={{ width: 36, height: 36, padding: 2, border: "1px solid #e5e7eb", borderRadius: 6, cursor: "pointer" }}
                  />
                  <Form.Control size="sm" value={settings.logoBgColor || "#3b82f6"} onChange={update("logoBgColor")} maxLength={7} style={{ maxWidth: 120 }} />
                </div>
                <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>Background color behind the logo icon in the sidebar</Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Logo & Favicon uploads */}
          <Row className="g-3 mt-2">
            <Col md={6}>
              <Form.Label className="sa-settings__label">LMS Logo</Form.Label>
              <div className="sa-branding-upload-box">
                {logoUrl ? (
                  <div className="sa-branding-preview">
                    <img src={logoUrl} alt="LMS Logo" />
                    <button type="button" className="sa-branding-remove" onClick={handleLogoDelete}><Trash2 size={14} /></button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="sa-branding-upload-btn"
                    onClick={() => logoInputRef.current?.click()}
                  >
                    <Upload size={16} /> Upload Logo
                  </button>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => e.target.files?.[0] && handleLogoUpload(e.target.files[0])}
                />
                {logoUrl && (
                  <button
                    type="button"
                    className="sa-branding-change-btn"
                    onClick={() => logoInputRef.current?.click()}
                  >
                    Change
                  </button>
                )}
              </div>
            </Col>
            <Col md={6}>
              <Form.Label className="sa-settings__label">LMS Favicon</Form.Label>
              <div className="sa-branding-upload-box">
                {faviconUrl ? (
                  <div className="sa-branding-preview">
                    <img src={faviconUrl} alt="Favicon" style={{ maxWidth: 48, maxHeight: 48 }} />
                    <button type="button" className="sa-branding-remove" onClick={handleFaviconDelete}><Trash2 size={14} /></button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="sa-branding-upload-btn"
                    onClick={() => faviconInputRef.current?.click()}
                  >
                    <Upload size={16} /> Upload Favicon
                  </button>
                )}
                <input
                  ref={faviconInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => e.target.files?.[0] && handleFaviconUpload(e.target.files[0])}
                />
                {faviconUrl && (
                  <button
                    type="button"
                    className="sa-branding-change-btn"
                    onClick={() => faviconInputRef.current?.click()}
                  >
                    Change
                  </button>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </div>

          <div className="sa-settings__tab-save">
            <AppButton variant="primary" size="sm" onClick={handleSave} disabled={saving}>
              <Save size={14} className="me-1" /> {saving ? "Saving…" : "Save Branding"}
            </AppButton>
          </div>
        </>
      )}

      {/* ═══ PAYMENTS TAB ═══ */}
      {activeTab === "payments" && (
        <>
          <div className="sa-card">
            <div className="sa-card-head">
              <CreditCard size={16} className="me-2" />Payment Gateway (Razorpay)
            </div>
            <div className="sa-card-body">
          <p style={{ fontSize: "0.8rem", color: "var(--text-tertiary, #9ca3af)", marginBottom: "1rem" }}>
            Configure Razorpay keys for plan upgrade payments. Institutes pay through this gateway when upgrading their subscription.
          </p>

          <Row className="g-3">
            <Col md={6}>
              <Form.Check
                type="switch"
                id="rz-enabled"
                label="Enable Razorpay Payments"
                checked={rz.enabled}
                onChange={rzUpdate("enabled")}
              />
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Mode</Form.Label>
                <Form.Select size="sm" value={rz.mode} onChange={rzUpdate("mode")}>
                  <option value="test">Test (Sandbox)</option>
                  <option value="live">Live (Production)</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <hr style={{ margin: "1rem 0", opacity: 0.1 }} />
          <h6 style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: "0.75rem" }}>Test Keys</h6>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Test Key ID</Form.Label>
                <Form.Control size="sm" value={rz.testKeyId} onChange={rzUpdate("testKeyId")} placeholder="rzp_test_..." />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Test Key Secret</Form.Label>
                <div className="d-flex gap-1">
                  <Form.Control
                    size="sm"
                    type={showSecrets.testKeySecret ? "text" : "password"}
                    value={rz.testKeySecret}
                    onChange={rzUpdate("testKeySecret")}
                    placeholder="Enter secret…"
                  />
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => toggleSecret("testKeySecret")} style={{ padding: "0.25rem 0.5rem" }}>
                    {showSecrets.testKeySecret ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </Form.Group>
            </Col>
          </Row>

          <hr style={{ margin: "1rem 0", opacity: 0.1 }} />
          <h6 style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: "0.75rem" }}>Live Keys</h6>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Live Key ID</Form.Label>
                <Form.Control size="sm" value={rz.liveKeyId} onChange={rzUpdate("liveKeyId")} placeholder="rzp_live_..." />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Live Key Secret</Form.Label>
                <div className="d-flex gap-1">
                  <Form.Control
                    size="sm"
                    type={showSecrets.liveKeySecret ? "text" : "password"}
                    value={rz.liveKeySecret}
                    onChange={rzUpdate("liveKeySecret")}
                    placeholder="Enter secret…"
                  />
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => toggleSecret("liveKeySecret")} style={{ padding: "0.25rem 0.5rem" }}>
                    {showSecrets.liveKeySecret ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </Form.Group>
            </Col>
          </Row>

          <hr style={{ margin: "1rem 0", opacity: 0.1 }} />
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Webhook Secret</Form.Label>
                <div className="d-flex gap-1">
                  <Form.Control
                    size="sm"
                    type={showSecrets.webhookSecret ? "text" : "password"}
                    value={rz.webhookSecret}
                    onChange={rzUpdate("webhookSecret")}
                    placeholder="Optional"
                  />
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => toggleSecret("webhookSecret")} style={{ padding: "0.25rem 0.5rem" }}>
                    {showSecrets.webhookSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                  Set this in Razorpay Dashboard → Webhooks. URL: <code>https://your-api.com/api/billing/webhook</code>
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <div style={{ fontSize: "0.8rem", marginTop: "1.5rem", color: "var(--text-tertiary)" }}>
                <strong>Status:</strong>{" "}
                {rz.enabled ? (
                  <span style={{ color: "#16a34a" }}>Enabled ({rz.mode})</span>
                ) : (
                  <span style={{ color: "#dc2626" }}>Disabled</span>
                )}
                {rz.mode === "test" && rz.hasTestKeys && " — Test keys configured ✓"}
                {rz.mode === "live" && rz.hasLiveKeys && " — Live keys configured ✓"}
              </div>
            </Col>
          </Row>
        </div>
      </div>
        </>
      )}

      {/* ═══ BILLING TAB — Billing card rendered under billing tab ═══ */}
      {activeTab === "billing" && (
        <>
          <div className="sa-card">
            <div className="sa-card-head">
              <Building2 size={16} className="me-2" />Billing & Company Details
            </div>
        <div className="sa-card-body">
          <p className="sa-settings__hint">
            Company details used on service invoices and tax invoices. These appear as the seller / "From" on every invoice PDF.
          </p>

          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Company / Legal Entity Name *</Form.Label>
                <Form.Control size="sm" value={billing.companyName} onChange={billingUpdate("companyName")} placeholder="e.g. Acme Technologies Pvt Ltd" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">GSTIN *</Form.Label>
                <Form.Control size="sm" value={billing.companyGstin} onChange={billingUpdate("companyGstin")} placeholder="e.g. 22AAAAA0000A1Z5" />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">PAN Number</Form.Label>
                <Form.Control size="sm" value={billing.companyPan} onChange={billingUpdate("companyPan")} placeholder="e.g. AAAAA1234A" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">SAC Code (Services)</Form.Label>
                <Form.Control size="sm" value={billing.sacCode} onChange={billingUpdate("sacCode")} placeholder="e.g. 998314" />
                <Form.Text className="sa-settings__sub-hint">HSN/SAC code for your service category (default: 998314 - IT services)</Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <hr className="sa-settings__divider" />
          <h6 style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: "0.75rem" }}>Company Address</h6>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Address</Form.Label>
                <Form.Control size="sm" value={billing.companyAddress} onChange={billingUpdate("companyAddress")} placeholder="Street / Locality" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">City</Form.Label>
                <Form.Control size="sm" value={billing.companyCity} onChange={billingUpdate("companyCity")} placeholder="e.g. Mumbai" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">State</Form.Label>
                <Form.Control size="sm" value={billing.companyState} onChange={billingUpdate("companyState")} placeholder="e.g. Maharashtra" />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="sa-settings__label">State Code</Form.Label>
                <Form.Control size="sm" value={billing.companyStateCode} onChange={billingUpdate("companyStateCode")} placeholder="e.g. 27" maxLength={2} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="sa-settings__label">PIN Code</Form.Label>
                <Form.Control size="sm" value={billing.companyPincode} onChange={billingUpdate("companyPincode")} placeholder="e.g. 400001" maxLength={6} />
              </Form.Group>
            </Col>
          </Row>

          <hr className="sa-settings__divider" />
          <h6 style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: "0.75rem" }}>Contact & GST on Service Invoices</h6>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Phone</Form.Label>
                <Form.Control size="sm" value={billing.companyPhone} onChange={billingUpdate("companyPhone")} placeholder="+91 98765 43210" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Email</Form.Label>
                <Form.Control size="sm" type="email" value={billing.companyEmail} onChange={billingUpdate("companyEmail")} placeholder="billing@company.com" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Website</Form.Label>
                <Form.Control size="sm" value={billing.companyWebsite} onChange={billingUpdate("companyWebsite")} placeholder="https://example.com" />
              </Form.Group>
            </Col>
          </Row>
          <Row className="g-3 mt-1">
            <Col md={4}>
              <Form.Check
                type="switch"
                id="billing-gst-enabled"
                label="Apply GST on service invoices"
                checked={billing.enableGst}
                onChange={billingUpdate("enableGst")}
              />
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">GST Rate (%)</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={billing.gstRate}
                  onChange={billingUpdate("gstRate")}
                  disabled={!billing.enableGst}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              {billing.enableGst && (
                <div className="sa-settings__tax-preview" style={{ marginTop: "1.5rem" }}>
                  <strong>Preview:</strong> ₹10,000 service →{" "}
                  ₹10,000 + GST ₹{Math.round(10000 * Number(billing.gstRate || 0) / 100).toLocaleString("en-IN")} ={" "}
                  <strong>₹{(10000 + Math.round(10000 * Number(billing.gstRate || 0) / 100)).toLocaleString("en-IN")}</strong>
                  {" "}(CGST {Number(billing.gstRate || 0) / 2}% + SGST {Number(billing.gstRate || 0) / 2}%)
                </div>
              )}
            </Col>
          </Row>

          <hr className="sa-settings__divider" />
          <h6 style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: "0.75rem" }}>Invoice Settings</h6>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Invoice Prefix</Form.Label>
                <Form.Control size="sm" value={billing.invoicePrefix} onChange={billingUpdate("invoicePrefix")} placeholder="e.g. LMS" maxLength={10} />
                <Form.Text className="sa-settings__sub-hint">Format: {billing.invoicePrefix || "LMS"}-{new Date().getFullYear()}-0001</Form.Text>
              </Form.Group>
            </Col>
            <Col md={3}>
              <div style={{ marginTop: "1.5rem" }}>
                <Form.Check
                  type="switch"
                  id="billing-reverse-charge"
                  label="Reverse Charge Applicable"
                  checked={billing.reverseCharge}
                  onChange={billingUpdate("reverseCharge")}
                />
              </div>
            </Col>
          </Row>

          <hr className="sa-settings__divider" />
          <h6 style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: "0.75rem" }}>Bank Details (shown on invoice)</h6>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Bank Name</Form.Label>
                <Form.Control size="sm" value={billing.bankName} onChange={billingUpdate("bankName")} placeholder="e.g. HDFC Bank" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Account Name</Form.Label>
                <Form.Control size="sm" value={billing.bankAccountName} onChange={billingUpdate("bankAccountName")} placeholder="Legal entity name" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Account Number</Form.Label>
                <Form.Control size="sm" value={billing.bankAccountNumber} onChange={billingUpdate("bankAccountNumber")} placeholder="XXXXXXXXXXXX" />
              </Form.Group>
            </Col>
          </Row>
          <Row className="g-3 mt-1">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">IFSC Code</Form.Label>
                <Form.Control size="sm" value={billing.bankIfscCode} onChange={billingUpdate("bankIfscCode")} placeholder="e.g. HDFC000XXXX" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Branch</Form.Label>
                <Form.Control size="sm" value={billing.bankBranch} onChange={billingUpdate("bankBranch")} placeholder="e.g. Whitefield, Bengaluru" />
              </Form.Group>
            </Col>
          </Row>

          <hr className="sa-settings__divider" />
          <h6 style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: "0.75rem" }}>Invoice Terms & Conditions</h6>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Control
                  as="textarea"
                  rows={3}
                  size="sm"
                  value={billing.invoiceTerms}
                  onChange={billingUpdate("invoiceTerms")}
                  placeholder="Each line becomes a separate term on the invoice..."
                  style={{ fontSize: "0.8rem" }}
                />
                <Form.Text className="sa-settings__sub-hint">One term per line. Shown at the bottom of every invoice PDF.</Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </div>
      </div>

          <div className="sa-settings__tab-save">
            <AppButton variant="primary" size="sm" onClick={handleBillingSave} disabled={saving}>
              <Save size={14} className="me-1" /> {saving ? "Saving…" : "Save Billing Details"}
            </AppButton>
            <AppButton variant="outline" size="sm" onClick={handleInvoicePreview} disabled={invoicePreviewLoading} className="ms-2">
              <Eye size={14} className="me-1" /> {invoicePreviewLoading ? "Generating…" : "Preview Plan Invoice"}
            </AppButton>
          </div>
        </>
      )}

      {/* ═══ Tax & Commission are in Payments tab ═══ */}
      {activeTab === "payments" && (
        <>
      <div className="sa-card">
        <div className="sa-card-head">
          <Receipt size={16} className="me-2" />Tax / GST Configuration
        </div>
        <div className="sa-card-body">
          <p className="sa-settings__hint">
            Configure tax applied on plan subscription payments. This will be shown as a separate line item during checkout.
          </p>

          <Row className="g-3">
            <Col md={6}>
              <Form.Check
                type="switch"
                id="tax-enabled"
                label="Enable Tax on Plan Payments"
                checked={tax.enabled}
                onChange={taxUpdate("enabled")}
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="tax-inclusive"
                label="Plan prices are tax-inclusive"
                checked={tax.includeInPrice}
                onChange={taxUpdate("includeInPrice")}
                disabled={!tax.enabled}
              />
              <Form.Text className="sa-settings__sub-hint">
                When ON, the displayed price already includes tax. When OFF, tax is added on top.
              </Form.Text>
            </Col>
          </Row>

          <hr className="sa-settings__divider" />
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Tax Label</Form.Label>
                <Form.Select size="sm" value={tax.label} onChange={taxUpdate("label")} disabled={!tax.enabled}>
                  <option value="GST">GST</option>
                  <option value="IGST">IGST</option>
                  <option value="VAT">VAT</option>
                  <option value="Tax">Tax</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Tax Percentage (%)</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={tax.percentage}
                  onChange={taxUpdate("percentage")}
                  disabled={!tax.enabled}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">GSTIN / Tax ID</Form.Label>
                <Form.Control
                  size="sm"
                  value={tax.gstNumber}
                  onChange={taxUpdate("gstNumber")}
                  placeholder="e.g. 22AAAAA0000A1Z5"
                  disabled={!tax.enabled}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Business / Legal Entity Name</Form.Label>
                <Form.Control
                  size="sm"
                  value={tax.businessName}
                  onChange={taxUpdate("businessName")}
                  placeholder="e.g. Acme Technologies Pvt Ltd"
                  disabled={!tax.enabled}
                />
                <Form.Text className="sa-settings__sub-hint">
                  Appears on payment invoices
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              {tax.enabled && (
                <div className="sa-settings__tax-preview">
                  <strong>Preview:</strong>{" "}
                  ₹1,000 plan →{" "}
                  {tax.includeInPrice ? (
                    <span>
                      ₹{Math.round(1000 / (1 + Number(tax.percentage || 0) / 100)).toLocaleString("en-IN")} + {tax.label} ₹{(1000 - Math.round(1000 / (1 + Number(tax.percentage || 0) / 100))).toLocaleString("en-IN")} = <strong>₹1,000</strong>
                    </span>
                  ) : (
                    <span>
                      ₹1,000 + {tax.label} ₹{Math.round(1000 * Number(tax.percentage || 0) / 100).toLocaleString("en-IN")} = <strong>₹{(1000 + Math.round(1000 * Number(tax.percentage || 0) / 100)).toLocaleString("en-IN")}</strong>
                    </span>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </div>
      </div>

      <div className="sa-card">
        <div className="sa-card-head">
          <Percent size={16} className="me-2" />Commission Configuration
        </div>
        <div className="sa-card-body">
          <p className="sa-settings__hint">
            Configure the platform commission charged on fee payments collected via the LMS payment gateway.
          </p>

          <Row className="g-3">
            <Col md={6}>
              <Form.Check
                type="switch"
                id="commission-enabled"
                label="Enable Commission on LMS Payments"
                checked={commission.enabled}
                onChange={commissionUpdate("enabled")}
              />
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Commission Type</Form.Label>
                <Form.Select size="sm" value={commission.type} onChange={commissionUpdate("type")} disabled={!commission.enabled}>
                  <option value="percentage">Percentage</option>
                  <option value="flat">Flat Fee per Transaction</option>
                  <option value="percentage_plus_flat">Percentage + Flat Fee</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <hr className="sa-settings__divider" />
          <Row className="g-3">
            {(commission.type === "percentage" || commission.type === "percentage_plus_flat") && (
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="sa-settings__label">Commission Percentage (%)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={commission.percentage}
                    onChange={commissionUpdate("percentage")}
                    disabled={!commission.enabled}
                  />
                </Form.Group>
              </Col>
            )}
            {(commission.type === "flat" || commission.type === "percentage_plus_flat") && (
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="sa-settings__label">Flat Fee (₹)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min={0}
                    step={1}
                    value={commission.flatFee}
                    onChange={commissionUpdate("flatFee")}
                    disabled={!commission.enabled}
                  />
                </Form.Group>
              </Col>
            )}
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Minimum Commission (₹)</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  step={1}
                  value={commission.minimumCommission}
                  onChange={commissionUpdate("minimumCommission")}
                  disabled={!commission.enabled}
                  placeholder="0 = no minimum"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Maximum Commission (₹)</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  step={1}
                  value={commission.maximumCommission}
                  onChange={commissionUpdate("maximumCommission")}
                  disabled={!commission.enabled}
                  placeholder="0 = no cap"
                />
              </Form.Group>
            </Col>
          </Row>

          {commission.enabled && (
            <div className="sa-settings__tax-preview">
              <strong>Preview (₹1,000 payment):</strong>{" "}
              {commission.type === "percentage" && (
                <span>Commission = {Number(commission.percentage || 0)}% = ₹{Math.round(1000 * Number(commission.percentage || 0) / 100)}</span>
              )}
              {commission.type === "flat" && (
                <span>Commission = ₹{Number(commission.flatFee || 0)}</span>
              )}
              {commission.type === "percentage_plus_flat" && (
                <span>Commission = {Number(commission.percentage || 0)}% + ₹{Number(commission.flatFee || 0)} = ₹{Math.round(1000 * Number(commission.percentage || 0) / 100) + Number(commission.flatFee || 0)}</span>
              )}
            </div>
          )}
        </div>
      </div>

          <div className="sa-settings__tab-save">
            <AppButton variant="primary" size="sm" onClick={handlePaymentsSave} disabled={saving}>
              <Save size={14} className="me-1" /> {saving ? "Saving…" : "Save Payment Settings"}
            </AppButton>
          </div>
        </>
      )}

      {/* ═══ EMAIL TAB ═══ */}
      {activeTab === "email" && (
        <>
      {/* ── Email Settings ── */}
      <div className="sa-card">
        <div className="sa-card-head">
          <Mail size={16} className="me-2" />Email Configuration (Amazon SES / Gmail)
        </div>
        <div className="sa-card-body">
          {emailMsg && (
            <div className={`alert alert-${emailMsg.type === "success" ? "success" : "danger"} py-1 px-2 mb-3`} style={{ fontSize: 13 }}>
              {emailMsg.text}
            </div>
          )}

          <Row className="g-3 mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Email Provider</Form.Label>
                <Form.Select size="sm" value={emailCfg.provider} onChange={emailUpdate("provider")}>
                  <option value="ses">Amazon SES (SMTP)</option>
                  <option value="gmail">Gmail SMTP (Legacy)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Sender Email</Form.Label>
                <Form.Control size="sm" value={emailCfg.senderEmail} onChange={emailUpdate("senderEmail")} placeholder="noreply@lmscampus.com" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Sender Display Name</Form.Label>
                <Form.Control size="sm" value={emailCfg.senderName} onChange={emailUpdate("senderName")} placeholder="LMS Campus" />
              </Form.Group>
            </Col>
          </Row>

          {emailCfg.provider === "ses" && (
            <Row className="g-3 mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="sa-settings__label">SES SMTP Host</Form.Label>
                  <Form.Control size="sm" value={emailCfg.ses?.host || ""} onChange={(e) => setEmailCfg(prev => ({ ...prev, ses: { ...prev.ses, host: e.target.value } }))} placeholder="email-smtp.us-east-1.amazonaws.com" />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="sa-settings__label">Port</Form.Label>
                  <Form.Control size="sm" type="number" value={emailCfg.ses?.port || 587} onChange={(e) => setEmailCfg(prev => ({ ...prev, ses: { ...prev.ses, port: Number(e.target.value) } }))} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="sa-settings__label">SMTP Username {emailCfg.ses?.user && <span className="text-muted">(saved)</span>}</Form.Label>
                  <Form.Control size="sm" value={sesUser} onChange={(e) => setSesUser(e.target.value)} placeholder={emailCfg.ses?.user || "Enter SMTP username"} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="sa-settings__label">SMTP Password {emailCfg.ses?.hasPassword && <span className="text-muted">(saved)</span>}</Form.Label>
                  <Form.Control size="sm" type="password" value={sesPass} onChange={(e) => setSesPass(e.target.value)} placeholder={emailCfg.ses?.hasPassword ? "••••••••" : "Enter SMTP password"} />
                </Form.Group>
              </Col>
            </Row>
          )}

          {emailCfg.provider === "gmail" && (
            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="sa-settings__label">Gmail User</Form.Label>
                  <Form.Control size="sm" value={emailCfg.gmail?.user || ""} onChange={(e) => setEmailCfg(prev => ({ ...prev, gmail: { ...prev.gmail, user: e.target.value } }))} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="sa-settings__label">Gmail App Password {emailCfg.gmail?.hasPassword && <span className="text-muted">(saved)</span>}</Form.Label>
                  <Form.Control size="sm" type="password" value={gmailPass} onChange={(e) => setGmailPass(e.target.value)} placeholder={emailCfg.gmail?.hasPassword ? "••••••••" : "Enter app password"} />
                </Form.Group>
              </Col>
            </Row>
          )}

          <Row className="g-3 mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Reply-To Email</Form.Label>
                <Form.Control size="sm" value={emailCfg.replyToEmail} onChange={emailUpdate("replyToEmail")} placeholder="Optional" />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Pool Size</Form.Label>
                <Form.Control size="sm" type="number" min={1} max={20} value={emailCfg.maxConnectionPool} onChange={emailUpdate("maxConnectionPool")} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Rate Limit (msgs/sec)</Form.Label>
                <Form.Control size="sm" type="number" min={1} max={50} value={emailCfg.rateLimit} onChange={emailUpdate("rateLimit")} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Queue Delay (ms)</Form.Label>
                <Form.Control size="sm" type="number" min={50} max={5000} step={50} value={emailCfg.queueDelay} onChange={emailUpdate("queueDelay")} />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex align-items-center gap-2 mt-3">
            <AppButton variant="primary" size="sm" onClick={handleEmailSave} disabled={emailSaving}>
              <Save size={14} className="me-1" />{emailSaving ? "Saving…" : "Save Email Settings"}
            </AppButton>

            <div className="d-flex align-items-center gap-2 ms-auto">
              <Form.Control size="sm" type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@example.com" style={{ width: 220 }} />
              <AppButton variant="outline" size="sm" onClick={handleTestEmail} disabled={testingSend || !testEmail}>
                <Send size={14} className="me-1" />{testingSend ? "Sending…" : "Send Test"}
              </AppButton>
            </div>
          </div>
        </div>
      </div>
        </>
      )}

      {/* ═══ INFO TAB ═══ */}
      {activeTab === "info" && (
        <div className="sa-card">
          <div className="sa-card-head">Platform Information</div>
          <div className="sa-card-body">
            <DetailRow label="Version" value="1.0.0" />
            <DetailRow label="Environment" value={process.env.NODE_ENV || "development"} />
            <DetailRow label="Last Deployed" value="19 Feb 2026, 10:00 AM" />
          </div>
        </div>
      )}

      {/* ── Plan Invoice Preview Modal ── */}
      <Modal show={showInvoicePreview} onHide={closeInvoicePreview} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Plan Invoice Preview (Sample Data)</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, height: "75vh" }}>
          {invoicePreviewUrl && (
            <iframe
              src={invoicePreviewUrl}
              title="Invoice Preview"
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SettingsPage;
